class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener('cart-drawer:refresh', (e) => this.refreshCartDrawer(e))
      return;
    }

    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");

      // Force Swiper instances inside the drawer to recalculate/initialize when drawer becomes visible
      setTimeout(() => {
        this.querySelectorAll("slideshow-section").forEach((slider) => {
          if (slider.swiper) {
            slider.swiper.update();
            slider.centerNavigation?.();
          } else {
            slider.swiperInitilize?.();
          }
        });
      }, 150);
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener('cart-drawer:refresh', (e) => this.refreshCartDrawer(e))
  }

  renderContents(parsedState, isClosedCart = true) {
    const addingCard = this.querySelector(".shoppable-product-card.is-adding");

    if (addingCard && parsedState.sections["cart-drawer"]) {
      // MULTI-STAGE PRECISE UPDATE FLOW:
      const parser = new DOMParser();
      const newDoc = parser.parseFromString(parsedState.sections["cart-drawer"], "text/html");
      const newInner = newDoc.querySelector("#CartDrawer");

      if (newInner) {
        // Stage 1: Update Header, Items, and Footer instantly so the item appears in the cart first!
        const selectorsToUpdate = [
          "#CartDrawer-TotalQty",
          ".wt-free-shipping-bar",
          "#CartDrawer-CartItems",
          ".wt-cart__drawer__footer",
          "#cart-errors"
        ];

        selectorsToUpdate.forEach((selector) => {
          const oldEl = this.querySelector(selector);
          const newEl = newInner.querySelector(selector);
          if (oldEl && newEl) {
            oldEl.innerHTML = newEl.innerHTML;
          }
        });

        // Also update the cart icon bubble instantly
        const bubbleSection = this.getSectionsToRender().find(s => s.id === "cart-icon-bubble");
        if (bubbleSection && parsedState.sections["cart-icon-bubble"]) {
          const bubbleEl = document.getElementById(bubbleSection.id);
          if (bubbleEl) {
            bubbleEl.innerHTML = this.getSectionInnerHTML(
              parsedState.sections["cart-icon-bubble"],
              bubbleSection.selector
            );
          }
        }

        // Stage 2: Smoothly fade out the added product card in the slider
        addingCard.style.setProperty("opacity", "0", "important");
        addingCard.style.setProperty("transform", "scale(0.85)", "important");
        addingCard.style.transition = "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)";

        // Stage 3: After the card has faded out, update the cross-sell slider HTML
        setTimeout(() => {
          const oldCrossSell = this.querySelector(".wt-cart__cross-sell");
          const newCrossSell = newInner.querySelector(".wt-cart__cross-sell");
          if (oldCrossSell && newCrossSell) {
            oldCrossSell.innerHTML = newCrossSell.innerHTML;
            
            // Re-initialize the slideshow since innerHTML changed
            const newSlider = oldCrossSell.querySelector("slideshow-section");
            if (newSlider) {
              newSlider.connectedCallback?.();
            }
          }
        }, 550);
      }
    } else {
      // Default instantaneous update behavior for standard cart actions
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (sectionElement) {
          sectionElement.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.id],
            section.selector,
          );
        }
      });
    }

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }

    // Notify swap widget to re-initialize after cart contents update
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('cart-drawer:refresh-complete'));
    }, 100);
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e){
    const sectionsToRender = this.getSectionsToRender();
    fetch(`${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`)
    .then(response => response.json())
    .then((response) => {
      const parsedState = {
        "sections": response
      }
      this.renderContents(parsedState, false);
      document.dispatchEvent(new CustomEvent('cart-drawer:refresh-complete'));
    })
    .catch(e => {
      console.log(e)
    })
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    // Override parent CartItems.onCartUpdate to prevent redundant, buggy fetch calls that pollute drawer markup
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
